import { SearchInput } from '@/components/searchInput';
import { PROJECT_TYPE_OPTIONS, REGION_FILTERS_OPTIONS } from '@/utils/constant/appConstant';
import { Button, DatePicker, Popover, Select, Space, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { IoCloseOutline, IoFunnelOutline } from 'react-icons/io5';
import { ORDER_STATUS_OPTIONS, PAYMENT_STATUS_OPTIONS, OrderStatus } from '../constants';
import { useAccountSearch } from '@/features/quotes-management/hooks/useFilterSearch';
import { useMemo } from 'react';

const { RangePicker } = DatePicker;

interface OrderFiltersProps {
  currentSearchText: string;
  currentStatus: OrderStatus | null;
  currentRegion: number | null;
  currentAccount: string | null;
  currentPaymentStatus: number | null;
  currentDateRange: [string, string] | null;
  currentProjectType: number | null;
  isRegionAll: boolean;
  userRegion?: number;
  statusFilterOptions: { value: number; label: string }[];
  onSearchChange: (searchText: string) => void;
  onStatusChange: (status: OrderStatus | null | undefined) => void;
  onRegionChange: (region: number | null | undefined) => void;
  onAccountChange: (accountId: string | null) => void;
  onPaymentStatusChange: (paymentStatus: number | null) => void;
  onDateRangeChange: (dateRange: [string, string] | null) => void;
  onProjectTypeChange: (projectType: number | null) => void;
  onApplyFilters: (filters: {
    status: OrderStatus | null;
    region: number | null;
    account: string | null;
    paymentStatus: number | null;
    dateRange: [string, string] | null;
    projectType: number | null;
  }) => void;
  onClearAll: () => void;
}

export const OrderFilters: React.FC<OrderFiltersProps> = ({
  currentSearchText: _currentSearchText,
  currentStatus,
  currentRegion,
  currentAccount,
  currentPaymentStatus,
  currentDateRange,
  currentProjectType,
  isRegionAll,
  statusFilterOptions,
  onSearchChange,
  onStatusChange,
  onRegionChange,
  onAccountChange,
  onPaymentStatusChange,
  onDateRangeChange,
  onProjectTypeChange,
  onApplyFilters,
  onClearAll,
}) => {
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [accountSearchText, setAccountSearchText] = useState('');

  // Draft state — only committed when "Apply Filters" is clicked
  const [draftStatus, setDraftStatus] = useState<OrderStatus | null>(currentStatus);
  const [draftRegion, setDraftRegion] = useState<number | null>(currentRegion);
  const [draftAccount, setDraftAccount] = useState<string | null>(currentAccount);
  const [draftPaymentStatus, setDraftPaymentStatus] = useState<number | null>(currentPaymentStatus);
  const [draftDateRange, setDraftDateRange] = useState<[string, string] | null>(currentDateRange);
  const [draftProjectType, setDraftProjectType] = useState<number | null>(currentProjectType);

  const { data: accountsData, isLoading: isLoadingAccounts } = useAccountSearch(accountSearchText, 20);

  const accountOptions = useMemo(() => {
    return (accountsData?.value || []).map((account) => ({
      label: account.name,
      value: account.accountid,
    }));
  }, [accountsData]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraftStatus(currentStatus);
      setDraftRegion(currentRegion);
      setDraftAccount(currentAccount);
      setDraftPaymentStatus(currentPaymentStatus);
      setDraftDateRange(currentDateRange);
      setDraftProjectType(currentProjectType);
    }
    setFilterPopoverOpen(open);
  };

  const handleApplyFilters = () => {
    onApplyFilters({
      status: draftStatus,
      region: draftRegion,
      account: draftAccount,
      paymentStatus: draftPaymentStatus,
      dateRange: draftDateRange,
      projectType: draftProjectType,
    });
    setFilterPopoverOpen(false);
  };

  const handleClearAll = () => {
    setDraftStatus(null);
    setDraftRegion(null);
    setDraftAccount(null);
    setDraftPaymentStatus(null);
    setDraftDateRange(null);
    setDraftProjectType(null);
    onClearAll();
  };

  const getFilterLabel = (type: string, value: string | number | null) => {
    if (value === null) return null;
    switch (type) {
      case 'region':
        return REGION_FILTERS_OPTIONS.find(opt => opt.value === value)?.label;
      case 'status':
        return ORDER_STATUS_OPTIONS.find(opt => opt.value === value)?.label;
      case 'account':
        return accountOptions.find(opt => opt.value === value)?.label ?? String(value);
      case 'paymentStatus':
        return PAYMENT_STATUS_OPTIONS.find(opt => opt.value === value)?.label;
      case 'projectType':
        return PROJECT_TYPE_OPTIONS.find(opt => opt.value === value)?.label;
      default:
        return null;
    }
  };

  const activeFilterCount = [
    currentStatus !== null,
    isRegionAll && currentRegion !== null,
    currentAccount !== null,
    currentPaymentStatus !== null,
    currentDateRange !== null,
    currentProjectType !== null,
  ].filter(Boolean).length;

  const draftDateRangeValue: [Dayjs, Dayjs] | null =
    draftDateRange
      ? [dayjs(draftDateRange[0]), dayjs(draftDateRange[1])]
      : null;

  return (
    <div className="mb-4">
      <div className="flex gap-3 items-center flex-wrap">
        {/* Search */}
        <div className="flex-1 min-w-[250px] max-w-[500px]">
          <SearchInput
            placeholder="Search by customer, reference, order number..."
            onChange={(e) => onSearchChange(e)}
            allowClear
          />
        </div>

        {/* More Filters Button */}
        <div>
          <Popover
            content={
              <div style={{ width: 400, maxHeight: '400px', display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{ overflowY: 'auto', overflowX: 'hidden', paddingRight: 8, paddingBottom: 16, flex: 1 }}
                  className="pr-2"
                  onWheel={(e) => e.stopPropagation()}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {/* Order Status Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Order Status
                      </label>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select status"
                        value={draftStatus === null ? undefined : draftStatus}
                        onChange={(value) => {
                          setDraftStatus(value === undefined || value === null ? null : value as OrderStatus);
                        }}
                        options={statusFilterOptions}
                        allowClear
                      />
                    </div>

                    {/* Payment Status Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Status
                      </label>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select payment status"
                        value={draftPaymentStatus === null ? undefined : draftPaymentStatus}
                        onChange={(value) => {
                          setDraftPaymentStatus(value === undefined || value === null ? null : value as number);
                        }}
                        options={PAYMENT_STATUS_OPTIONS}
                        allowClear
                      />
                    </div>

                    {/* Region Filter */}
                    {isRegionAll && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Region
                        </label>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="Select region"
                          value={draftRegion === null ? undefined : draftRegion}
                          onChange={(value) => {
                            setDraftRegion(value === undefined || value === null ? null : value as number);
                          }}
                          options={REGION_FILTERS_OPTIONS}
                          allowClear
                        />
                      </div>
                    )}

                    {/* Account Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Account (Customer)
                      </label>
                      <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="Search and select account"
                        value={draftAccount === null ? undefined : draftAccount}
                        onChange={(value) => {
                          setDraftAccount(value === undefined || value === null ? null : value as string);
                        }}
                        onSearch={(value) => setAccountSearchText(value)}
                        filterOption={false}
                        options={accountOptions}
                        loading={isLoadingAccounts}
                        allowClear
                        notFoundContent={isLoadingAccounts ? 'Loading...' : 'No accounts found'}
                      />
                    </div>

                    {/* Project Type Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Project Type
                      </label>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select project type"
                        value={draftProjectType === null ? undefined : draftProjectType}
                        onChange={(value) => {
                          setDraftProjectType(value === undefined || value === null ? null : value as number);
                        }}
                        options={PROJECT_TYPE_OPTIONS}
                        allowClear
                      />
                    </div>

                    {/* Date Range Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Order Date Range
                      </label>
                      <RangePicker
                        style={{ width: '100%' }}
                        value={draftDateRangeValue}
                        onChange={(dates) => {
                          if (!dates || !dates[0] || !dates[1]) {
                            setDraftDateRange(null);
                          } else {
                            setDraftDateRange([
                              dates[0].startOf('day').toISOString(),
                              dates[1].endOf('day').toISOString(),
                            ]);
                          }
                        }}
                        format="DD-MM-YYYY"
                      />
                    </div>
                  </Space>
                </div>

                {/* Footer */}
                <div
                  className="pt-3 px-2 flex flex-row justify-end"
                  style={{ borderTop: '1px solid #f0f0f0', background: 'white', flexShrink: 0 }}
                >
                  <Space style={{ width: '100%' }}>
                    {activeFilterCount > 0 && (
                      <Button block onClick={handleClearAll}>
                        Clear All
                      </Button>
                    )}
                    <Button type="primary" block onClick={handleApplyFilters}>
                      Apply Filters
                    </Button>
                  </Space>
                </div>
              </div>
            }
            title={
              <div className="flex items-center justify-between">
                <span>Filter Options</span>
                {activeFilterCount > 0 && (
                  <span className="text-xs text-gray-500">{activeFilterCount} active</span>
                )}
              </div>
            }
            trigger="click"
            open={filterPopoverOpen}
            onOpenChange={handleOpenChange}
            placement="bottomRight"
            overlayStyle={{ zIndex: 1050 }}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
          >
            <Button icon={<IoFunnelOutline />} className='h-[44px]'>
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-primary-100 text-primary-600 rounded-full text-xs font-medium">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </Popover>
        </div>
      </div>

      {/* Active Filters Tags */}
      {activeFilterCount > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {currentStatus !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onStatusChange(null)}>
              Status: {getFilterLabel('status', currentStatus)}
            </Tag>
          )}
          {currentPaymentStatus !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onPaymentStatusChange(null)}>
              Payment: {getFilterLabel('paymentStatus', currentPaymentStatus)}
            </Tag>
          )}
          {isRegionAll && currentRegion !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onRegionChange(null)}>
              Region: {getFilterLabel('region', currentRegion)}
            </Tag>
          )}
          {currentAccount !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onAccountChange(null)}>
              Account: {getFilterLabel('account', currentAccount)}
            </Tag>
          )}
          {currentProjectType !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onProjectTypeChange(null)}>
              Project Type: {getFilterLabel('projectType', currentProjectType)}
            </Tag>
          )}
          {currentDateRange !== null && (
            <Tag closable color={"#dc3b41"} onClose={() => onDateRangeChange(null)}>
              Date: {dayjs(currentDateRange[0]).format('DD-MM-YYYY')} – {dayjs(currentDateRange[1]).format('DD-MM-YYYY')}
            </Tag>
          )}
          <Button
            type="link"
            size="small"
            className='text-primary-500'
            icon={<IoCloseOutline />}
            onClick={onClearAll}
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
};

export default OrderFilters;
